import {useEffect, useState} from 'react';

import {useCookieConsentContext} from '@use-cookie-consent/react';

import {useModal} from 'UI/hooks/useModal';

/**
 * The `useCookieConsent` hook manages the cookie consent functionality including initial consent modal,
 * consent manager modal, and floating consent indicator. This hook handles the automatic display of the
 * initial consent modal after a delay when no consent decision has been made, and manages the visibility
 * of the floating consent indicator once a consent decision exists.
 *
 * @returns An object containing modal controls and state for cookie consent management.
 * - `closeInitModal` - Function to close the initial consent modal.
 * - `closeManager` - Function to close the consent manager modal.
 * - `InitModal` - Component for the initial consent modal.
 * - `ManagerModal` - Component for the consent manager modal.
 * - `openInitModal` - Function to open the initial consent modal.
 * - `openManager` - Function to open the consent manager modal.
 * - `showFloating` - Boolean indicating whether to show the floating consent indicator.
 *
 * @example
 * ```tsx
 * const {
 *     InitModal,
 *     ManagerModal,
 *     openManager,
 *     showFloating
 * } = useCookieConsent();
 *
 * return (
 *     <>
 *         <InitModal />
 *         <ManagerModal />
 *         {showFloating && (
 *             <FloatingConsentButton onClick={openManager} />
 *         )}
 *     </>
 * );
 * ```
 */
export const useCookieConsent = () => {
    const {closeModal: closeInitModal, Modal: InitModal, openModal: openInitModal} = useModal();
    const {closeModal: closeManager, Modal: ManagerModal, openModal: openManager} = useModal();
    const {consent} = useCookieConsentContext();
    const [showFloating, setShowFloating] = useState(false);

    useEffect(() => {
        // eslint-disable-next-line no-undef
        let timer: NodeJS.Timeout;

        if (typeof consent.statistics === 'undefined') {
            timer = setTimeout(() => {
                openInitModal();
            }, 500);
        }

        return () => {
            clearTimeout(timer);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openInitModal]);

    useEffect(() => {
        if (typeof consent.statistics === 'boolean') {
            setShowFloating(true);
        }
    }, [consent]);

    return {
        closeInitModal,
        closeManager,
        InitModal,
        ManagerModal,
        openInitModal,
        openManager,
        showFloating
    };
};